import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import { PUBLIC_NAV_ITEMS, FOOTER_PRIMARY_LINKS, FOOTER_SECONDARY_LINKS } from "../constants/navigation";
import MobileBottomNav from "../components/MobileBottomNav";

/* ============================================================================
   BB-067A: Elvis Aloha Archive — Header & Footer
   
   PRODUCTION VERSION - Canonical Navigation Structure
   Navigation: Collection | Arena | Tickets | Research | About
   
   All public pages use this shared navigation configuration.
   DO NOT add page-specific navigation items.
   ============================================================================ */

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  
  /* Use canonical navigation from shared constants */
  const navItems = PUBLIC_NAV_ITEMS.map(item => ({
    label: item.label.toUpperCase(),
    to: item.href,
    matches: item.matches,
    includeChildren: item.includeChildren
  }));
  
  /* Normalize path for comparison */
  const normalizePath = (value) => {
    if (!value || value === "/") return "/";
    return value.replace(/\/+$/, "");
  };
  
  /* Check if nav item is active - explicit route ownership */
  const isNavItemActive = (item) => {
    const currentPath = normalizePath(location.pathname);
    if (currentPath === "/") return false;
    return item.matches.some((route) => {
      const normalizedRoute = normalizePath(route);
      if (currentPath === normalizedRoute) return true;
      return item.includeChildren === true && currentPath.startsWith(`${normalizedRoute}/`);
    });
  };

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setSearchOpen(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);
  
  return (
    <>
      <style>{`
        /* ===== COMPACT HEADER - Matches Homepage Navigation ===== */
        
        /* Desktop: Single row with logo + name + nav */
        @media (min-width: 901px) {
          .desktop-nav { display: flex !important; }
          .mobile-menu-btn { display: none !important; }
          .site-header { height: 82px !important; min-height: 82px !important; }
          .site-header-inner { 
            height: 82px !important;
            max-width: 1400px !important;
            margin-inline: auto !important;
            padding: 0 48px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
          }
          .public-header__brand { 
            display: flex !important;
            align-items: center !important;
            gap: 14px !important;
            flex-shrink: 0 !important;
          }
          .site-logo-img { 
            height: 54px !important;
            width: auto !important;
            object-fit: contain !important;
          }
          .site-name {
            font-family: 'Cormorant Garamond', serif !important;
            font-size: 18px !important;
            font-weight: 600 !important;
            color: #0D273C !important;
            letter-spacing: 0.06em !important;
            white-space: nowrap !important;
          }
          .desktop-nav { gap: 6px !important; }
        }
        
        /* Narrower desktop */
        @media (min-width: 901px) and (max-width: 1200px) {
          .site-header-inner { padding: 0 32px !important; }
          .desktop-nav { gap: 4px !important; }
          .site-logo-img { height: 48px !important; }
          .site-name { font-size: 16px !important; }
        }
        
        /* Mobile/Tablet: Hide nav, show hamburger */
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: inline-flex !important; }
          .site-header { height: 72px !important; min-height: 72px !important; }
          .site-header-inner { 
            height: 72px !important;
            width: 100% !important;
            padding: 0 16px !important;
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
          }
          .site-logo-img { height: 46px !important; width: auto !important; }
          .site-name { font-size: 15px !important; }
          .header-controls { flex-shrink: 0 !important; }
        }
        
        /* Very narrow mobile */
        @media (max-width: 480px) {
          .site-header-inner { padding: 0 12px !important; gap: 8px !important; }
          .site-logo-img { height: 42px !important; }
          .site-name { display: none !important; }
        }
        
        /* Navigation link styles */
        .public-header__nav-link {
          position: relative;
          display: inline-flex;
          align-items: center;
          min-height: 44px;
          padding: 4px 12px;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.055em;
          text-transform: uppercase;
          color: #0D273C;
          text-decoration: none;
          white-space: nowrap;
          transition: color 0.2s ease;
        }
        
        .public-header__nav-link::after {
          content: "";
          position: absolute;
          right: 12px;
          bottom: 4px;
          left: 12px;
          height: 2px;
          background: #D85C4A;
          opacity: 0;
          transform: scaleX(0);
          transform-origin: center;
          transition: opacity 160ms ease, transform 160ms ease;
        }
        
        .public-header__nav-link:hover {
          color: #8B7355;
        }
        
        .public-header__nav-link:hover::after {
          opacity: 0.5;
          transform: scaleX(0.6);
        }
        
        .public-header__nav-link.is-active {
          color: #D85C4A;
        }
        
        .public-header__nav-link.is-active::after {
          opacity: 1;
          transform: scaleX(1);
        }
        
        .public-header__nav-link:focus-visible {
          outline: 2px solid #D85C4A;
          outline-offset: 2px;
        }
      `}</style>
      
      {/* ===== HEADER ===== */}
      <header 
        className="site-header"
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          height: '82px',
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E5E5E5'
        }}
        data-testid="main-header"
      >
        <div 
          className="site-header-inner"
          style={{
            maxWidth: '1400px',
            height: '82px',
            marginInline: 'auto',
            padding: '0 48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          {/* Logo + Site Name */}
          <Link 
            to="/" 
            data-testid="site-logo" 
            className="public-header__brand"
            style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              textDecoration: 'none',
              flexShrink: 0
            }}
          >
            <img 
              className="site-logo-img"
              src="/assets/branding/elvis-logo-illustration.png"
              alt="" 
              style={{
                height: '54px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
            <span 
              className="site-name"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: '18px',
                fontWeight: 600,
                color: '#0D273C',
                letterSpacing: '0.06em',
                whiteSpace: 'nowrap'
              }}
            >
              ELVIS ALOHA COLLECTION
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav 
            className="desktop-nav" 
            aria-label="Primary navigation"
            style={{ 
              display: 'none',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {navItems.map((item) => {
              const active = isNavItemActive(item);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`public-header__nav-link${active ? ' is-active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Right controls */}
          <div 
            className="header-controls"
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              flexShrink: 0 
            }}
          >
            {/* Search button */}
            <button 
              onClick={() => setSearchOpen(!searchOpen)}
              style={{ 
                padding: '10px',
                color: '#0D273C',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                minWidth: '44px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              aria-label="Search the archive"
            >
              <Search style={{ width: '20px', height: '20px' }} />
            </button>
            
            {/* Mobile Menu Button - SHOWN via CSS on mobile only */}
            <button 
              className="mobile-menu-btn"
              style={{ 
                padding: '10px',
                color: '#0D273C',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                minWidth: '44px',
                minHeight: '44px',
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X style={{ width: '24px', height: '24px' }} /> : <Menu style={{ width: '24px', height: '24px' }} />}
            </button>
          </div>
        </div>
        
        {/* Search Bar */}
        {searchOpen && (
          <div 
            style={{ 
              position: 'absolute',
              left: 0,
              right: 0,
              top: '100%',
              padding: '16px',
              backgroundColor: 'rgba(247, 241, 229, 0.98)',
              borderBottom: '1px solid rgba(181, 138, 60, 0.45)'
            }}
          >
            <form onSubmit={handleSearch} style={{ maxWidth: '500px', margin: '0 auto' }}>
              <div style={{ position: 'relative' }}>
                <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: '#8A6928' }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search the archive..."
                  style={{
                    width: '100%',
                    paddingLeft: '40px',
                    paddingRight: '12px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    border: '1px solid rgba(181, 138, 60, 0.45)',
                    backgroundColor: 'white',
                    color: '#0D273C',
                    fontFamily: "'Source Sans 3', sans-serif",
                    fontSize: '16px',
                    outline: 'none',
                    borderRadius: '4px'
                  }}
                  autoFocus
                />
              </div>
            </form>
          </div>
        )}
      </header>

      {/* Spacer for fixed header - matches desktop/mobile heights */}
      <div className="header-spacer" style={{ height: '82px' }}>
        <style>{`
          @media (max-width: 900px) {
            .header-spacer { height: 72px !important; }
          }
        `}</style>
      </div>
      
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          style={{ 
            position: 'fixed',
            inset: 0,
            top: '72px',
            zIndex: 150,
            backgroundColor: '#FFFFFF',
            overflowY: 'auto'
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <nav style={{ padding: '24px 16px' }}>
            {navItems.map((item) => {
              const active = isNavItemActive(item);
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className={active ? 'mobile-nav-link is-active' : 'mobile-nav-link'}
                  aria-current={active ? 'page' : undefined}
                  style={{
                    display: 'block',
                    padding: '16px',
                    fontFamily: "'Source Sans 3', sans-serif",
                    fontSize: '16px',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: active ? '#D85C4A' : '#0D273C',
                    backgroundColor: active ? '#F3F4F6' : 'transparent',
                    textDecoration: 'none',
                    borderRadius: '4px',
                    marginBottom: '4px',
                    minHeight: '44px'
                  }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
          
          {/* Close button at bottom */}
          <div style={{ padding: '16px', borderTop: '1px solid #E5E5E5' }}>
            <button
              onClick={() => setMobileMenuOpen(false)}
              style={{
                width: '100%',
                padding: '16px',
                fontFamily: "'Source Sans 3', sans-serif",
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#0D273C',
                backgroundColor: 'transparent',
                border: '1px solid rgba(181, 138, 60, 0.45)',
                borderRadius: '4px',
                cursor: 'pointer',
                minHeight: '44px'
              }}
            >
              Close Menu
            </button>
          </div>
        </div>
      )}
    </>
  );
};

/* ============================================================================
   FOOTER — Deep Navy with Archive Identity
   ============================================================================ */
export const Footer = () => (
  <footer 
    style={{ 
      backgroundColor: '#0D273C',
      color: '#F7F1E5',
      padding: 'clamp(48px, 8vw, 80px) clamp(16px, 4vw, 48px) clamp(32px, 4vw, 48px)'
    }}
    data-testid="main-footer"
  >
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Footer Grid */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
        gap: 'clamp(32px, 4vw, 48px)',
        marginBottom: 'clamp(32px, 4vw, 48px)'
      }}>
        {/* Brand Column - Dedicated Footer Mark */}
        <div>
          <a 
            href="/"
            aria-label="Elvis Aloha Collection - Home"
            className="footer-brand-mark-link"
            style={{ display: 'block' }}
          >
            <img 
              src="/icons/logo-dark-bg.png"
              alt="Elvis Aloha Collection"
              className="footer-brand-mark"
              style={{ 
                width: 'clamp(80px, 10vw, 120px)', 
                height: 'auto',
                marginBottom: '14px',
                display: 'block'
              }}
            />
          </a>
          <p style={{ 
            fontFamily: "'Source Serif 4', serif",
            fontSize: '14px',
            lineHeight: 1.6,
            color: '#E8E2D8',
            maxWidth: '260px'
          }}>
            Preserving the documented history of Elvis Presley&apos;s historic 
            1973 Aloha from Hawaii broadcast.
          </p>
        </div>
        
        {/* Collection Links */}
        <div>
          <h4 style={{ 
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#8A6928',
            marginBottom: '16px'
          }}>
            Collection
          </h4>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { to: '/collection?category=tickets', label: 'Tickets' },
              { to: '/collection?category=documents', label: 'Documents' },
              { to: '/collection?category=artifacts', label: 'Artifacts' },
              { to: '/collection?category=photographs', label: 'Photographs' },
            ].map(link => (
              <Link 
                key={link.to}
                to={link.to}
                style={{ 
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '14px',
                  color: '#D9E7E5',
                  textDecoration: 'none'
                }}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
        
        {/* Research Links */}
        <div>
          <h4 style={{ 
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#8A6928',
            marginBottom: '16px'
          }}>
            Registry
          </h4>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { to: '/ticket-registry/map', label: 'Arena Map' },
              { to: '/ticket-registry/list', label: 'Ticket List' },
              { to: '/research', label: 'Research' },
              { to: '/facts', label: 'Facts & Evidence' },
            ].map(link => (
              <Link 
                key={link.to}
                to={link.to}
                style={{ 
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '14px',
                  color: '#D9E7E5',
                  textDecoration: 'none'
                }}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
        
        {/* About Links */}
        <div>
          <h4 style={{ 
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#8A6928',
            marginBottom: '16px'
          }}>
            About
          </h4>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { to: '/about-archive', label: 'About the Archive' },
              { to: '/governance', label: 'Governance' },
              { to: '/rights', label: 'Rights & Reproductions' },
              { to: '/accessibility', label: 'Accessibility' },
              { to: '/privacy', label: 'Privacy & Legal' },
            ].map(link => (
              <Link 
                key={link.to}
                to={link.to}
                style={{ 
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '14px',
                  color: '#D9E7E5',
                  textDecoration: 'none'
                }}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
      
      {/* Divider */}
      <div style={{ 
        borderTop: '1px solid rgba(247, 241, 229, 0.2)',
        paddingTop: 'clamp(24px, 3vw, 32px)'
      }}>
        {/* Independent Archive Disclaimer */}
        <p style={{ 
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '12px',
          lineHeight: 1.65,
          color: '#C8D4D2',
          maxWidth: '900px',
          marginBottom: '16px'
        }}>
          <strong style={{ color: '#C9A227' }}>Independent Historical Archive:</strong> Elvis Aloha Archive 
          is an independent historical and educational archive. It is not affiliated with, endorsed by, 
          sponsored by, or officially connected with Elvis Presley Enterprises, Graceland, Sony Music 
          Entertainment, or any official Elvis Presley organization. Names, images, trademarks, and 
          historical materials remain the property of their respective rights holders and are presented 
          for historical, educational, documentary, and archival purposes.
        </p>
        
        {/* Copyright */}
        <p style={{ 
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '13px',
          color: '#A0B0AE'
        }}>
          © {new Date().getFullYear()} Elvis Aloha Archive. Independent historical archive.
        </p>
      </div>
    </div>
  </footer>
);

/* ============================================================================
   PUBLIC LAYOUT WRAPPER
   ============================================================================ */
const PublicLayout = ({ children }) => {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <main id="main-content" style={{ flex: 1 }}>
        {children}
      </main>
      <Footer />
      <MobileBottomNav />
      {/* Clearance so footer content is never hidden behind the fixed mobile tab bar */}
      <div className="mobile-nav-clearance" aria-hidden="true" />
      <style>{`
        .mobile-nav-clearance { display: none; }
        @media (max-width: 900px) {
          .mobile-nav-clearance { display: block; height: calc(60px + env(safe-area-inset-bottom, 0px)); }
        }
      `}</style>
    </div>
  );
};

export default PublicLayout;
